import { useCallback, useEffect, useMemo, useState } from "react";
import { ONLINE_THRESHOLD_MS, PREVIEW_MAX_B64_LENGTH } from "../constants";
import type {
  HeartbeatMsg,
  NegotiateResponse,
  PreviewCache,
  VrStateMsg,
} from "../types";
import { useWebPubSubClient, type Negotiation } from "./useWebPubSubClient";

type Params = {
  apiBaseUrl: string;
  /** stable token getter — see useFreshToken */
  getToken: () => Promise<string | null>;
  enabled: boolean;
  /**
   * false ignores any preview frame a heartbeat carries, keeping the rest of it
   * (presence, meta). Frames can arrive unasked: the headset streams until its session
   * ends once anyone has asked. Defaults to true.
   */
  acceptPreview?: boolean;
  /** the ticking clock; only used to evict previews of headsets that went quiet */
  now: number;
};

export type HeadsetRealtime = {
  /** our own socket, as opposed to whether a headset is beating */
  wsConnected: boolean;
  /** every headset ever seen, most recently heard from first */
  headsetIds: string[];
  lastSeenByHeadset: Record<string, number>;
  metaByHeadset: Record<string, any>;
  vrStateByHeadset: Record<string, VrStateMsg>;
  previewByHeadset: Record<string, PreviewCache>;
};

/**
 * The operator side of the realtime channel: what to negotiate, and what the two
 * message kinds the headset emits mean.
 *
 * The connection underneath belongs to useWebPubSubClient, which owns the socket,
 * the reconnect and the group join and knows nothing about headsets.
 *
 * Headset ids are never removed once seen, so a headset you are about to put on
 * stays selectable while offline; only its preview frame is evicted.
 */
export function useHeadsetRealtime({
  apiBaseUrl,
  getToken,
  enabled,
  acceptPreview = true,
  now,
}: Params): HeadsetRealtime {
  const [lastSeenByHeadset, setLastSeenByHeadset] = useState<Record<string, number>>({});
  const [metaByHeadset, setMetaByHeadset] = useState<Record<string, any>>({});
  const [vrStateByHeadset, setVrStateByHeadset] = useState<Record<string, VrStateMsg>>({});
  const [previewByHeadset, setPreviewByHeadset] = useState<Record<string, PreviewCache>>({});

  // Drop stale frames, so a headset that went quiet and came back shows "waiting for
  // preview frame" rather than the last thing it happened to be looking at.
  useEffect(() => {
    setPreviewByHeadset((prev) => {
      let changed = false;
      const next: Record<string, PreviewCache> = { ...prev };
      for (const [id, v] of Object.entries(prev)) {
        if (now - v.seenAt > ONLINE_THRESHOLD_MS) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [now]);

  /**
   * Stable on purpose: this is what owns the connection, so a new identity per
   * render would tear the socket down and rebuild it every second. getToken is
   * stable for the same reason — see useFreshToken.
   */
  const negotiate = useCallback(async (): Promise<Negotiation> => {
    const token = await getToken();
    // no token means the Keycloak refresh failed; App.tsx logs out on its own
    // interval, so keep retrying rather than giving up on a transient failure
    if (!token) throw new Error("no token");

    const res = await fetch(`${apiBaseUrl}/realtime/negotiate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ clientType: "operator" }),
    });
    if (!res.ok) throw new Error(`negotiate failed: ${res.status}`);

    const data: NegotiateResponse = await res.json();
    return { url: data.url, group: data.tenantGroup };
  }, [apiBaseUrl, getToken]);

  const handleMessage = useCallback((payload: any) => {
    if (!payload?.type) return;

    if (payload.type === "headset.heartbeat") {
      const hb = payload as HeartbeatMsg;
      const id = (hb.headsetId ?? "").trim();
      if (!id) return;

      if (acceptPreview && hb.preview?.b64 && hb.preview?.mime) {
        const p = hb.preview;
        // basic sanity: reject huge images
        if (p.b64.length < PREVIEW_MAX_B64_LENGTH) {
          const seenAt = Date.now();
          setPreviewByHeadset((prev) => ({ ...prev, [id]: { preview: p, seenAt } }));
        }
      }

      setLastSeenByHeadset((prev) => ({ ...prev, [id]: Date.now() }));
      if (hb.meta !== undefined) {
        setMetaByHeadset((prev) => ({ ...prev, [id]: hb.meta }));
      }
      return;
    }

    if (payload.type === "vr.state") {
      const st = payload as VrStateMsg;
      const id = (st.headsetId ?? "").trim();
      if (!id) return;

      setVrStateByHeadset((prev) => ({ ...prev, [id]: st }));
    }
  }, [acceptPreview]);

  const wsConnected = useWebPubSubClient({ negotiate, enabled, onMessage: handleMessage });

  const headsetIds = useMemo(() => {
    const ids = Object.keys(lastSeenByHeadset);
    ids.sort((a, b) => (lastSeenByHeadset[b] ?? 0) - (lastSeenByHeadset[a] ?? 0));
    return ids;
  }, [lastSeenByHeadset]);

  return {
    wsConnected,
    headsetIds,
    lastSeenByHeadset,
    metaByHeadset,
    vrStateByHeadset,
    previewByHeadset,
  };
}
